import APIService from '../api';
import {
  openCentralizedPopupWindow,
  onSigninPopupCloseRedirect
} from '@/utils/windowPopup';
import { insideProviderIframe } from '@/utils/providerIframe';
import { SOCIAL_LOGIN_REDIRECT_KEY } from './SigninService';
import segmentIo from '@/utils/segment.io';
import { IBusinessGoogleLocationData } from '@/models/business';

export default class SignUpService extends APIService {
  createManager(data: any, recaptcha?: string) {
    return this.post('/business/signup', data, {
      ...(recaptcha && { headers: { recaptcha } })
    });
  }

  connectSquare() {
    return this.get('/merchant-connect/square/oauth/signup?json=true');
  }

  connectBigcommerce(shop?: string) {
    return this.get(
      `/merchant-connect/bigcommerce/oauth/signup?json=true&redirectToDashboard=true&shop=${shop}`
    );
  }

  connectShopify(shop?: string) {
    return this.get(
      `/merchant-connect/shopify/oauth/signup?json=true&redirectToDashboard=true&shop=${shop}`
    );
  }

  connectWix(oauthId?: string | null) {
    return this.get(
      `/merchant-connect/wix/oauth/signup?json=true&redirectToDashboard=true&oauthId=${oauthId}`
    );
  }

  preValidate(business: any) {
    return this.post('/business/signup/pre-validation', business);
  }

  createBusiness(business: any) {
    return this.post('/admin/business/register', business);
  }

  businessLead(business: any) {
    return this.post('/admin/business-lead', business);
  }

  createBusinessWithProvider(business: any, oauthToken: string) {
    return this.post('/merchant-connect/business/signup', {
      ...business,
      oauthToken
    });
  }

  businessProspect(data: Record<string, any>): Promise<any> {
    return this.post('/admin/business/prospect', data);
  }

  setupRules(rules: any) {
    if (rules.refundsLimitDays === '') {
      rules.refundsLimitDays = 0;
    }

    return this.put('/v2/admin/business/loyalty-settings', rules);
  }

  getCategories({ version = 1 } = {}) {
    if (version === 2) {
      return this.get('/v2/category');
    }

    return this.get('/category');
  }

  getAdresses(search: string | undefined, countryCode = 'US') {
    return this.get(
      `/v2/location/address?q=${encodeURIComponent(
        search || ''
      )}&countryIso2=${countryCode}`
    );
  }

  getGoogleAdresses(search: string | undefined) {
    return this.get(
      `/admin/business-location/autocomplete?input=${encodeURIComponent(
        search || ''
      )}`
    );
  }

  getGoogleAdressesDetail(
    placeId: string
  ): Promise<{ data: IBusinessGoogleLocationData }> {
    return this.get(`/admin/business-location/autocomplete/${placeId}`);
  }

  getCitiesV2(search: string | undefined, countryCode = 'US') {
    return this.get(
      `/v2/location/city?q=${encodeURI(
        search || ''
      )}&countryIso2=${countryCode}`
    );
  }

  storeLead(email: string, provider?: string, origin?: string) {
    const props: any = {
      email,
      provider
    };
    if (origin) props.origin = origin;

    return this.post('/lead/signup', props);
  }

  async verifyIfEmailExists(email: string) {
    return this.get(`/auth/email?email=${encodeURIComponent(email)}`);
  }

  signupWithProvider(
    provider: 'google' | 'apple',
    sessionData?: { referrerURL: string; redirect?: string }
  ) {
    if (sessionData) {
      localStorage.setItem(
        SOCIAL_LOGIN_REDIRECT_KEY,
        JSON.stringify(sessionData)
      );
    }

    const url = `${this.host()}/auth/${provider}/authUrl/signup?anonymousId=${segmentIo.getAnonymousId()}`;

    if (insideProviderIframe()) {
      const newWindow = (openCentralizedPopupWindow(
        url,
        `${provider} Connect`,
        450,
        600
      ) as unknown) as Window;

      onSigninPopupCloseRedirect(newWindow);
    } else {
      location.href = url;
    }
  }

  signUp(data: any) {
    return this.post('/admin/business/register', data);
  }

  getStates() {
    return this.get(
      '/location/state?filters=countryCode:US&limit=100&sort=name:ASC'
    );
  }

  setRefundsDays(data: { refundsLimitDays: number }) {
    return this.put('/admin/business/refunds', data);
  }

  getCities(state: string, cityName?: string) {
    let base = `/location/city`;
    base += '?skip=0';
    base += '&limit=1000';
    if (state) base += `&filters=stateCode:${state}`;
    if (cityName) base += `&search=${cityName}&searchableFields=name`;
    base += '&sort=name:ASC';
    return this.get(base);
  }
}
